import { RegXUtils } from '@/utils'
import { CommonHTTP } from '@/abstract'
import { URL_ADD_PRODUCT, URL_CHECK_DOMAIN, URL_CONF_PRODUCT } from '@/utils'
import { HostingModel, ServerModel } from '@/models'
import { DomainCheckResponseData } from '@/core/ryanada'

export class ProductHTTP extends CommonHTTP {
  private regX: RegXUtils
  constructor(regX: RegXUtils) {
    super()
    this.regX = regX
  }

  private handleDomainNotRequired(
    product: HostingModel | ServerModel,
    url: string
  ): number {
    const cartIndexParam = this.regX.extractCartIndexParam(url)

    if (!cartIndexParam) {
      location.reload()
      return -1
    }

    const cartIndex = this.regX.extractCartIndex(cartIndexParam)
    return Number.parseInt(cartIndex)
  }

  private async getConfigurationURL(
    product: HostingModel | ServerModel
  ): Promise<{ configurationURL?: string; added: boolean }> {
    const pid = product.id

    const response = await fetch(`${URL_ADD_PRODUCT}&pid=${pid}`, {
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error('ProductHTTP: adding product failed')
    }

    if (this.regX.hasConfigureProduct(response.url)) {
      return { configurationURL: response.url, added: false }
    }

    if (
      response.redirected &&
      !/[?&]a=view(?:&|$)/u.test(response.url)
    ) {
      throw new Error('ProductHTTP: unexpected add product redirect')
    }

    return { added: true }
  }

  private async checkDomain(
    product: HostingModel | ServerModel
  ): Promise<number> {
    const requestData = product.getRequestData()
    const token = requestData.formData.get('token')
    const pid = product.id
    const domain = product.domain

    if (!token) {
      throw new Error('ProductHTTP: token is undefined')
    }

    if (!pid) {
      throw new Error('ProductHTTP: pid is undefined')
    }

    if (!domain) {
      throw new Error('ProductHTTP: domain is undefined')
    }
    const formData = new FormData()
    formData.append('token', token.toString())
    formData.append('pid', pid)
    formData.append('domain', domain)
    formData.append('sld', domain)
    formData.append('type', 'incart')
    formData.append('tld', '')
    formData.append('source', 'cartAddDomain')

    const response = await fetch(URL_CHECK_DOMAIN, {
      method: 'POST',
      credentials: 'include',
      body: formData
    })

    const responseData: DomainCheckResponseData = await response.json()

    if (responseData.result.length < 1) {
      return -1
    }

    return responseData.result[0].num
  }

  private async addToCart(
    product: HostingModel | ServerModel,
    cartIndex: string
  ): Promise<void> {
    const requestData = product.getAcceptedRequestData(cartIndex)

    const response = await fetch(URL_CONF_PRODUCT, {
      method: 'POST',
      credentials: 'include',
      body: requestData.formData
    })

    this.handleReloadPage(response)
  }

  async post(product: HostingModel | ServerModel): Promise<boolean> {
    if (product.isInCart) {
      await this.addToCart(product, product.cartIndex as string)
      return true
    }

    if (product.requiresDomain && !product.domain) {
      throw new Error('ProductHTTP: domain is required but not provided')
    }

    if (product.requiresDomain) {
      const cartIndex = await this.checkDomain(product)
      void this.addToCart(product, cartIndex.toString())
      return true
    } else {
      const result = await this.getConfigurationURL(product)
      if (result.configurationURL) {
        const cartIndex = this.handleDomainNotRequired(
          product,
          result.configurationURL
        )
        void this.addToCart(product, cartIndex.toString())
        return true
      }
      if (result.added) {
        location.reload()
      }
      return result.added
    }
  }
}
